/*
 * portafolio.js · PORTAFOLIO horizontal por pisos — yanipaper-3d
 * =============================================================
 * Reemplaza la vieja "constelación" del Acto III por el PORTAFOLIO rescatado del prototipo:
 * varios SCROLL HORIZONTALES apilados. Cada PISO es una sección editorial del CMS (uno por
 * sección): corre horizontal mientras está clavado (pin), y al terminar el scroll vertical
 * avanza al siguiente piso, que vuelve a correr en horizontal. Rescata `horizontalGallery()`
 * del prototipo, generalizada a N pisos.
 *
 * Tamaños variables (rescatados del feed estilo "verge" del sitio grande): cada tarjeta es
 * 'lg'/'md'/'sm' según IMPORTANCIA (destacado) + RECENCIA (fecha) — ver buildPisos() en cms/feed.js.
 *
 * DATOS: vienen del CMS propio (cms/feed.js · loadFeed). Este módulo solo pinta y anima.
 * SEGURIDAD: todo el contenido del CMS se inyecta con createElement/textContent — NUNCA innerHTML.
 * ACCESIBILIDAD: el DOM se pinta SIEMPRE (HALL-A: legible sin JS de scroll). El pin horizontal
 * es realce de escritorio; en móvil/reduced-motion el CSS lo degrada a rejilla vertical.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { buildPisos, fechaLegible } from './cms/feed.js';

/* ------------------------------------------------------------------ */
/* Render (siempre) — pinta los pisos y sus tarjetas desde el feed     */
/* ------------------------------------------------------------------ */
export function renderPortafolio(container) {
  if (!container) return;
  const pisos = buildPisos();
  container.replaceChildren();
  for (const piso of pisos) container.appendChild(renderPiso(piso));
}

function renderPiso(piso) {
  const sec = document.createElement('section');
  sec.className = 'piso';
  sec.dataset.seccion = piso.slug;
  sec.setAttribute('aria-label', `Trabajo — ${piso.name}`);

  const pin = el('div', 'h-pin');
  const track = el('div', 'h-track');

  // Intro del piso (portada de la sección): SOLO el título (sin balazo ni descripción).
  // h3: cuelga de "Mi trabajo" (h2) → jerarquía h1(tesis) › h2(Mi trabajo) › h3(sección).
  const intro = el('div', 'piso-intro');
  intro.append(el('h3', 'piso-titulo', piso.name));
  track.appendChild(intro);

  // TRES NIVELES DE IMPORTANCIA EDITORIAL — cada nivel ocupa el MISMO ÁREA (la de la principal):
  const posts = piso.posts;
  // Nivel 1 — 1 imagen (principal, la más reciente).
  if (posts[0]) track.appendChild(renderCard(posts[0], 'lead'));
  // Nivel 2 — 2 imágenes en ese mismo espacio.
  const duo = posts.slice(1, 3);
  if (duo.length) track.appendChild(renderGroup(duo, 'is-duo'));
  // Nivel 3 — grupos de 4 imágenes (2×2), cada grupo en ese mismo espacio.
  const resto = posts.slice(3);
  for (let i = 0; i < resto.length; i += 4) {
    track.appendChild(renderGroup(resto.slice(i, i + 4), 'is-quad'));
  }

  pin.appendChild(track);
  sec.appendChild(pin);
  return sec;
}

/* Un GRUPO de tarjetas (2 o 4) que ocupa el mismo footprint que la principal. La rejilla la fija el CSS. */
function renderGroup(items, cls) {
  const g = el('div', `card-group ${cls}`);
  items.forEach((p) => g.appendChild(renderCard(p, 'cell')));
  return g;
}

// Saneamiento PREVENTIVO de enlaces del CMS: hoy `enlace` es constante hardcodeada (sin riesgo), pero
// cuando loadFeed() pase a fetch/Payload un `javascript:`/`data:` sería XSS. Solo se admite http(s) y
// mailto; cualquier otra cosa cae al ancla interna del slug.
function safeHref(enlace, slug) {
  const fallback = `#${slug}`;
  if (typeof enlace !== 'string' || !enlace) return fallback;
  try {
    const u = new URL(enlace, window.location.origin);
    if (u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:') return enlace;
  } catch { /* URL inválida */ }
  return fallback;
}

function renderCard(post, nivel) {
  // Enlace: interno al slug (blog/<slug>.html en el CMS) o externo (saneado) si lo trae el post.
  const a = document.createElement('a');
  a.className = `work-card is-${nivel}`; // nivel: 'lead' · 'pair' · 'small'
  const href = safeHref(post.enlace, post.slug);
  a.href = href;
  // Los posts (/blog/<slug>.html) y el ancla interna #slug abren en la MISMA pestaña. Solo un enlace
  // externo REAL (otro origen) abre pestaña nueva.
  if (!href.startsWith('#')) {
    try {
      const u = new URL(href, window.location.origin);
      if (u.origin !== window.location.origin) { a.target = '_blank'; a.rel = 'noopener'; }
    } catch { /* URL relativa/ inválida → interna, misma pestaña */ }
  }

  // Arte / portada
  const art = el('div', 'card-art');
  if (post.cover) {
    const img = document.createElement('img');
    img.src = post.cover;              // ruta del CMS (assets/images/…)
    img.alt = '';                       // decorativa; el título da el nombre accesible
    img.loading = 'lazy';
    img.setAttribute('aria-hidden', 'true');
    art.appendChild(img);
  } else {
    art.classList.add('card-art--empty');
    art.appendChild(el('span', 'card-empty', '> sin portada'));
  }
  a.appendChild(art);

  // Tag DEMO (honestidad de placeholder)
  a.appendChild(el('span', 'card-tag', 'DEMO'));

  // Cuerpo: meta (sección · fecha) + título + excerpt
  const body = el('div', 'card-body');
  const meta = el('p', 'card-meta');
  meta.append(el('time', 'card-fecha', fechaLegible(post.fecha)));
  if (post.tipo === 'pieza-especial') meta.append(el('span', 'card-pieza', 'pieza especial'));
  body.append(
    meta,
    el('h3', 'card-titulo', post.title),
    el('p', 'card-excerpt', post.excerpt || '')
  );
  a.appendChild(body);
  return a;
}

/* ------------------------------------------------------------------ */
/* Motion (solo cinema/escritorio) — pin + scrub horizontal por piso   */
/* ------------------------------------------------------------------ */
/*
 * Un ScrollTrigger por cada `.h-pin`: mientras el piso está clavado, el scroll VERTICAL se
 * traduce en desplazamiento HORIZONTAL del track (x: -(scrollWidth - innerWidth)). Al terminar
 * el track, se suelta el pin y el scroll vertical sigue hasta el siguiente piso. Rescatado de
 * horizontalGallery() del prototipo, generalizado a N pisos. Devuelve un teardown.
 */
export function setupPisosHorizontal(container) {
  if (!container) return () => {};
  const triggers = [];
  // Marca el modo horizontal: el CSS por defecto pinta los pisos como rejilla VERTICAL (HALL-A);
  // solo con esta clase (cinema/escritorio) el track pasa a nowrap + pin. Así, sin JS, sigue legible.
  container.classList.add('is-horizontal');

  gsap.utils.toArray(container.querySelectorAll('.piso .h-pin')).forEach((pin) => {
    const track = pin.querySelector('.h-track');
    if (!track) return;
    const amount = () => Math.max(0, track.scrollWidth - window.innerWidth);

    const tween = gsap.to(track, {
      x: () => -amount(),
      ease: 'none',
      scrollTrigger: {
        trigger: pin,
        start: 'top top',
        end: () => '+=' + amount(),
        pin: true,
        // scrub DIRECTO (sin lag): el track alcanza -amount() JUSTO cuando el pin libera, así el
        // scroll horizontal TERMINA antes de que arranque el vertical. Con scrub:1 quedaba a medias.
        scrub: true,
        invalidateOnRefresh: true,
        anticipatePin: 1,
      },
    });
    triggers.push(tween.scrollTrigger, tween);

    // (Sin reveal por-tarjeta.) Antes cada card hacía fade-in con `start:'left 92%'`, pero las cards
    // que YA estaban en pantalla al clavarse el piso no se revelaban hasta tarde → aparecían justo
    // cuando el título se iba. Ahora las cards se ven DESDE el inicio, junto al título; su "entrada"
    // es el propio desplazamiento horizontal.
  });

  return () => {
    triggers.forEach((t) => t && t.kill && t.kill());
    container.classList.remove('is-horizontal');
  };
}

/* ------------------------------------------------------------------ */
/* util                                                               */
/* ------------------------------------------------------------------ */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text; // SIEMPRE textContent (dato del CMS), nunca innerHTML
  return node;
}
